import { useEffect, useState, useCallback, useRef } from 'react'
import { User as UserIcon } from 'lucide-react'
import { apiService } from '@/service/api'
import { USER_AVATAR_GRADIENT_CLASS } from '@/utils/userUtils'
import { cn } from '@/lib/utils'

interface User {
  user_id: string
  email: string | null
  name: string | null
  picture: string | null
}

interface MentionAutocompleteProps {
  query: string
  onSelect: (user: User) => void
  onClose: () => void
  position: { top: number; left: number }
}

const MentionAutocomplete = ({
  query,
  onSelect,
  onClose,
  position,
}: MentionAutocompleteProps) => {
  const [users, setUsers] = useState<User[]>([])
  // Per-row, because one dead photo URL must not blank the whole list. A
  // removed Google photo leaves a URL that no longer resolves, and with no
  // handler the browser paints its broken-image placeholder — a question mark
  // in Safari.
  //
  // Keyed by the URL, NOT the user id (greptile #30765): a re-search can return
  // a different, healthy photo for the same person, and an id-keyed set would
  // keep suppressing it for the lifetime of the component — `searchUsers`
  // replaces `users` but would not clear this. Keying the URL also means the
  // same dead URL is not retried on every render, so there is no flicker loop.
  const [failedPhotos, setFailedPhotos] = useState<ReadonlySet<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const searchUsers = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      // For empty query, show all users
      setIsLoading(true)
      try {
        const results = await apiService.searchOrganizationUsers('a') // Search with 'a' to get some results
        setUsers(results)
        setSelectedIndex(0)
      } catch (error) {
        console.error('Failed to search users:', error)
        setUsers([])
      } finally {
        setIsLoading(false)
      }
      return
    }

    setIsLoading(true)
    try {
      const results = await apiService.searchOrganizationUsers(searchQuery)
      setUsers(results)
      setSelectedIndex(0)
    } catch (error) {
      console.error('Failed to search users:', error)
      setUsers([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      searchUsers(query)
    }, 300)

    return () => clearTimeout(timer)
  }, [query, searchUsers])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (users.length === 0) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev + 1) % users.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev - 1 + users.length) % users.length)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (users[selectedIndex]) {
          onSelect(users[selectedIndex])
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [users, selectedIndex, onSelect, onClose])

  // Always show the autocomplete when active, even with empty query
  return (
    <div
      ref={containerRef}
      className="fixed z-50 bg-white dark:bg-dark-card border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden"
      style={{
        top: position.top,
        left: position.left,
        minWidth: '280px',
        maxWidth: '320px',
      }}
    >
      {isLoading ? (
        <div
          className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400"
        >
          Searching...
        </div>
      ) : users.length > 0 ? (
        <div className="max-h-60 overflow-y-auto">
          {users.map((user, index) => (
            <button
              key={user.user_id}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onSelect(user)
              }}
              className={cn(
                "w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors text-left",
                index === selectedIndex ? "bg-gray-100 dark:bg-dark-accent" : "bg-transparent"
              )}
            >
              {user.picture && !failedPhotos.has(user.picture) ? (
                <img
                  src={user.picture}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="w-8 h-8 rounded-full object-cover"
                  onError={() => {
                    // Capture the prop, not `currentTarget.src` — the browser
                    // reports a resolved absolute URL, which would not match
                    // the `has(user.picture)` check above.
                    const dead = user.picture
                    if (dead) {
                      setFailedPhotos((prev) => new Set(prev).add(dead))
                    }
                  }}
                />
              ) : (
                <div
                  className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold',
                    USER_AVATAR_GRADIENT_CLASS
                  )}
                >
                  {/* A glyph, never a literal '?' — this used to render the
                      character itself when a member had neither name nor email,
                      which is indistinguishable from a broken image. */}
                  {(user.name || user.email)?.trim()?.charAt(0)?.toUpperCase() || (
                    <UserIcon className="w-4 h-4" aria-hidden />
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div
                  className="font-medium truncate text-gray-900 dark:text-dark-text"
                >
                  {user.name || 'Unnamed User'}
                </div>
                {user.email && (
                  <div
                    className="text-sm truncate text-gray-600 dark:text-gray-400"
                  >
                    {user.email}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div
          className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400"
        >
          No users found
        </div>
      )}
    </div>
  )
}

export default MentionAutocomplete
